export type CaveatSeverity = "high" | "medium" | "low";

export interface Caveat {
  type: string;
  severity: CaveatSeverity;
  message: string;
  affectedIds?: unknown;
}

export interface ComparisonCost {
  fuel: number;
  wage: number;
  dumpFees: number;
  maintenance: number; // notional per-mile wear; grouped under penalties, not operating
  operating: number; // real cash outlay: fuel+wage+dump (the headline)
  // Not-billed dollars: notional maintenance + the optimizer's objective penalties
  // (tuning dollars) — scored on both sides with the run's config; 0 when unused.
  compactness: number;
  overtime: number;
  timeWindow: number;
  endTime: number;
  balance: number;
  penalties: number; // maintenance + the soft penalties above
  total: number; // operating + penalties
}

export interface ComparisonMetrics {
  driversUsed: number;
  dumpStops: number;
  drivingSeconds: number;
  serviceSeconds: number;
  totalSeconds: number;
  meters: number;
  cost: ComparisonCost;
}

export interface ComparisonDeltas {
  driversUsed: number;
  drivingSeconds: number;
  meters: number;
  costTotal: number;
}

export interface ScheduleComparison {
  id: string;
  date: string;
  fleetOptimizationId: string;
  benchmarkOptionIndex: number;
  benchmark: ComparisonMetrics;
  actual: ComparisonMetrics;
  deltas: ComparisonDeltas;
  comparable: boolean;
  caveats: Caveat[];
  createdBy: { type: string; id: string; name: string };
  createdAt: string;
}

const METERS_PER_MILE = 1609.34;

export const milesFromMeters = (m: number): number => m / METERS_PER_MILE;
export const hoursFromSeconds = (s: number): number => s / 3600;

export const deltaTone = (n: number): "worse" | "better" | "neutral" =>
  n > 0 ? "worse" : n < 0 ? "better" : "neutral";

export const hasHighCaveat = (caveats: Caveat[]): boolean =>
  caveats.some((c) => c.severity === "high");
